/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';

const messages = defineMessages({
    failedTitle: {
        id: 'clu.useInitializeCluProject.failedTitle',
        defaultMessage: 'Failed to open project'
    },
    failedContent: {
        id: 'clu.useInitializeCluProject.failedContent',
        defaultMessage: 'A server occurred while trying to open your project. Please refresh and try again.'
    }
});

export const useInitializeCluProject = ({ projectName, csAccountPath }: { projectName: string; csAccountPath: string }) => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const projectTracker = useProgressTracker({
        persistOnComplete: true,
        onComplete: () => {
            if (FeatureToggles.CluGptGeneration) {
                dispatcher.dispatch('fetchCluGptJobs', { toastData: {} });
                dispatcher.dispatch('fetchGeneratedUtterancesWithGpt', { toastData: {} });
            }
        }
    });

    const { azureStore } = rootStore;
    const currentProject = rootStore.currentCluProjectStore;
    const currentSelectedCsStore = azureStore.session.csAccountStore;

    const openCluProject = () =>
        dispatcher.dispatch('openCluProject', {
            projectName,
            tracker: projectTracker,
            toastData: { failed: { title: localize(messages.failedTitle), content: localize(messages.failedContent) } }
        });

    const switchAzureCsAccountTracker = useProgressTracker({
        onComplete: openCluProject
    });

    React.useEffect(() => {
        if (projectTracker.isNotStarted) {
            // In case of a resource path available as a query param in the url, the resource is switched first then the project is opened.
            if (csAccountPath) {
                dispatcher.dispatch('switchAzureCsAccountInSession', { csAccountPath, tracker: switchAzureCsAccountTracker });
            } else {
                openCluProject();
            }
        } else if (projectTracker.isCompleted) {
            dispatcher.dispatch('pollCluTrainingJobs', { projectName, toastData: {} });
        }
    }, [currentProject, projectTracker.state]);

    React.useEffect(() => {
        if (currentProject?.csAccountPath && currentProject?.csAccountPath !== currentSelectedCsStore?.path) {
            history.push(routeTable[currentProject.projectType === 'Orchestration' ? 'orchestration' : 'clu'].root());
        }
    }, [currentSelectedCsStore, currentProject]);

    React.useEffect(() => () => dispatcher.dispatch('closeCluProject'), []);

    return { isLoaded: projectTracker.isCompleted, isFailed: projectTracker.isFailed };
};
